import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import {isAndroidOrHarmony, isIos, isPcOrLinux, isPlaystation, isSwitch, PlatformUtil} from 'utils/platform';
import { DataDetailUtil } from 'components/exception/issue/DataDetail';
import { Col, Row, Spin } from 'antd';
import { isNotNullish, isNullish } from 'utils/nullish';
import scss from './RegisterInfo.scss';
import { ErrorBoundary } from 'react-error-boundary';
import { ze } from 'utils/zhEn';

/**
 * @param {string[]} lines
 * @param {RegExp} headerRegex
 * @return {{ header: string, bodyLines: string[] }[]}
 */
export function makeSectionsFromLinesByHeaderRegex(lines, headerRegex) {
  const result = [];
  let currentSection = null;
  (lines || []).forEach(line => {
    if (headerRegex.test(line)) {
      if (isNotNullish(currentSection)) {
        result.push(currentSection);
      }
      currentSection = { header: line, bodyLines: [] };
    } else {
      if (isNullish(currentSection)) {
        currentSection = { header: '', bodyLines: [] };
      }
      currentSection.bodyLines.push(line);
    }
  });

  if (isNotNullish(currentSection)) {
    result.push(currentSection);
  }
  return result;
}

/**
 * @param {string[]} lines
 * @return {{ header: string, registerKvPairs: [string, string][] }[]}
 */
function parseAndroidLines(lines) {
  const sections = makeSectionsFromLinesByHeaderRegex(lines, /info/);
  return sections.map(({ header, bodyLines }) => {
    const registerSlices = bodyLines.join(' ').split(/\s/).filter(x => x);
    const registerKvPairs = registerSlices.map(x => {
      const [k, ...other] = x.split('=');
      return [k, other.join('')];
    });
    return {
      header,
      registerKvPairs,
    }
  });
}

/**
 * @param {string[]} lines
 * @return {{ header: string, registerKvPairs: [string, string][] }[]}
 */
function parseIosLines(lines) {
  const sections = makeSectionsFromLinesByHeaderRegex(lines, /Thread/);
  return sections.map(({ header, bodyLines }) => {
    const registerSlices = bodyLines.join(' ').split(/\s/).filter(x => x);
    const registerKvPairs = registerSlices.map(x => {
      const [k, ...other] = x.split(':');
      return [k, other.join('')];
    });
    return {
      header,
      registerKvPairs,
    }
  });
}

/**
 * @param {string[]} lines
 * @return {{ header: string, registerKvPairs: [string, string][] }[]}
 */
function parseMinidumpLines(lines) {
  const sections = makeSectionsFromLinesByHeaderRegex(lines, /^\d/);
  return sections.map(({ header, bodyLines }) => {
    const registerSlices = [...bodyLines.join('\n')
      .matchAll(/[a-zA-Z0-9]+[^\S\r\n]*=[^\S\r\n]*[a-zA-Z0-9]+/g)] // 匹配 rax=0x123之类，等号两边允许有空格
      .map(x => x[0]);
    const registerKvPairs = registerSlices.map(x => {
      const [k, ...other] = x.split('=');
      return [k.trim(), other.join('').trim()];
    });
    return {
      header,
      registerKvPairs,
    }
  });
}

function makeRegisterDom(registerKvPairs) {
  if (!registerKvPairs) {
    return [];
  }
  const longestKeyLength = Math.max(...registerKvPairs.map(([k, v]) => (k || '').length));
  const regKeyMinWidth = 20 + Math.min(longestKeyLength, 6) * 8;

  const cols = registerKvPairs.map((kvPair, i) => {
    const [k, v] = kvPair;
    const span = v?.length > 20 ? 12 : 6;
    return <Col
      key={i}
      span={span}
    ><div className={scss.reg}>
      <div
        className={scss.regKey}
        style={{
          minWidth: `${regKeyMinWidth}px`
        }}
      >{ k || '-' }</div>
      <div className={scss.regValue}>{ v || '-' }</div>
    </div></Col>;
  });
  return <div
    className={scss.registerInfoContainer}
  >
    <Row
      className={scss.regInfoBody}
      gutter={[10, 10]}
    >{ cols }</Row>
  </div>;
}

export const RegisterInfoUtil = Object.freeze({
  parseMinidumpLines,
  makeRegisterDom,

  /**
   * @return {{ header: string, registerKvPairs: [string, string][] }[]}
   * */
  getParseResult(currentIssueObj, pid) {
    const {
      attachList,
      crashDoc,
      pcRegisterInfoFetching,
      pcRegisterInfoLines,
      minidumpRegisterInfoParseResult,
      pcModuleInfoList,
      pcAllModuleInfoLines,
      isShowingMinidumpStack,
      retracedAnrTrace,
    } = currentIssueObj;
    const { esMap } = crashDoc || {};
    const { modelBit } = esMap || {};

    let lines = [];
    /** @type {{ header: string, registerKvPairs: [string, string][] }[]} */
    let parseResult = [];
    if (!isShowingMinidumpStack && (isAndroidOrHarmony(pid) || isSwitch(pid))) {
      lines = DataDetailUtil.makeRegisterInfoTextLines(pid, attachList, modelBit);
      parseResult = parseAndroidLines(lines);
    } else if (!isShowingMinidumpStack && PlatformUtil.isIosOrMac(pid)) {
      lines = DataDetailUtil.makeRegisterInfoTextLines(pid, attachList, modelBit);
      parseResult = parseIosLines(lines);
    } else if (isShowingMinidumpStack || isPcOrLinux(pid) || isPlaystation(pid)) {
      lines = pcRegisterInfoLines || [];
      parseResult = minidumpRegisterInfoParseResult || [];
    }
    return parseResult;
  },
});

const RegisterInfo = ({
  issue,
  pid,
}) => {
  const issueObj = useMemo(() => issue.toJS() || {}, [issue]);

  const {
    pcRegisterInfoFetching,
    isShowingMinidumpStack,
  } = issueObj;

  /** @type {{ header: string, registerKvPairs: [string, string][] }[]} */
  let parseResult = RegisterInfoUtil.getParseResult(issueObj, pid);

  const sectionDomList = parseResult.map(({ header, registerKvPairs }, i) => {
    const registerDom = makeRegisterDom(registerKvPairs);

    return <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} key={i}>
      { (!isAndroidOrHarmony(pid) || isShowingMinidumpStack) && <div>{ header }</div> }
      { registerDom }
    </div>;
  });

  const dom = (!pcRegisterInfoFetching && (sectionDomList || []).length === 0)
    ? <div>{ ze('无数据', 'No Data') }</div>
    : <div className={scss.regInfoBody} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>{ sectionDomList }</div>;

  return <div
    className={scss.registerInfoContainer}
  ><Spin spinning={!!pcRegisterInfoFetching}>{ dom }</Spin></div>;
};

RegisterInfo.propTypes = {
  reduxState: PropTypes.object,
  issue: PropTypes.object,
  pid: PropTypes.string,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(RegisterInfo);
